import { type ContextDto, PartyType } from "Infrastructure/Api/Api";
import { type AccessRoleCode, AccessRoleCodes } from "Models/AccessRoleCodes";
import { Resources, type TFunction } from "Translations/Resources";

const COMPONENT_RESOURCES = Resources.Auth.ContextSwitcher;

const PERSONAL_ROLES = new Set<AccessRoleCode>([
  "Customer",
  "Hunter",
  "HunterPlus",
  "Admin",
  "AdminLite",
  "Aml",
  "CustomerOnboarding",
]);

const buildPersonName = (
  firstName?: string | null,
  lastName?: string | null,
): string | null => {
  const name = `${firstName ?? ""} ${lastName ?? ""}`.trim();
  return name.length > 0 ? name : null;
};

/**
 * Display name of the context's own Party (company for Merchant/Employee/Disponent).
 * Onboarding-incomplete merchants have no CompanyName yet — fall back to the Party email
 * so the switcher can still distinguish them from each other.
 */
const getPartyDisplayName = ({
  partyType,
  partyFirstName,
  partyLastName,
  partyCompanyName,
  partyEmail,
}: ContextDto): string | null => {
  if (!partyType) {
    return null;
  }

  if (partyType === PartyType.LegalEntity) {
    return partyCompanyName?.trim() ? partyCompanyName : (partyEmail ?? null);
  }

  return buildPersonName(partyFirstName, partyLastName) ?? partyEmail ?? null;
};

/**
 * Builds the two-line label for a context row. The API intentionally ships raw name fields
 * (party + identity person) and leaves display formatting to the frontend.
 * - Primary = the distinguishing identifier: company name for Merchant/Employee/Disponent,
 *   person's name (from Identity.Party) for personal roles. Falls back to role label if neither.
 * - Secondary = role label, with a "(Personal account)" hint for Customer to make it obvious
 *   why two contexts can share the same person name.
 */
export const getContextDisplay = (
  context: ContextDto,
  t: TFunction,
): { primary: string; secondary: string } => {
  const { accessRoleCode, identityFirstName, identityLastName } = context;

  // accessRoleCode arrives as a raw string from the API; fall back to the bare code
  // if the backend ever sends a role the frontend doesn't have a label for yet.
  const localizedRole =
    COMPONENT_RESOURCES.Roles[accessRoleCode as AccessRoleCode];
  const roleLabel =
    localizedRole !== undefined ? t(localizedRole) : accessRoleCode;
  const isPersonalRole = PERSONAL_ROLES.has(accessRoleCode as AccessRoleCode);

  // Merchant / Employee / Disponent: company name on top, role below.
  if (!isPersonalRole) {
    return {
      primary: getPartyDisplayName(context) ?? roleLabel,
      secondary: roleLabel,
    };
  }

  // Customer / Hunter / Admin / etc.: person name on top, role below.
  // Customer gets a hint so the user can tell their personal context apart from look-alikes.
  const identityPersonName = buildPersonName(
    identityFirstName,
    identityLastName,
  );
  const primary =
    identityPersonName ?? getPartyDisplayName(context) ?? roleLabel;
  const secondary =
    accessRoleCode === AccessRoleCodes.Customer
      ? `${roleLabel} · ${t(COMPONENT_RESOURCES.PersonalAccountHint)}`
      : roleLabel;
  return { primary, secondary };
};
